// ─────────────────────────────────────────────────────────────────────────────
// WHEN THE DAY TURNS OVER.  Copied from app/dayBoundary.ts (via web/).
//
// The classroom has its own database, so nothing forces this to match the app —
// it is copied so a flashcard here behaves exactly like one in Gibberfish: a
// day ends at 03:00 local, so reviewing just after midnight still counts as
// "tonight", not tomorrow.
//
// The boundary is 03:00 LOCAL, not midnight and never UTC — the app has shipped
// the UTC bug more than once, and its author is on UTC-3 where `toISOString()`
// rolls the date over at 21:00.
// ─────────────────────────────────────────────────────────────────────────────

/** The hour, local, at which one logical day becomes the next. */
export const DAY_RESET_HOUR = 3;

/** The calendar date the logical day is NAMED for: the date it started on. */
export function dayKey(now: Date = new Date()): string {
  const d = new Date(now);
  d.setHours(d.getHours() - DAY_RESET_HOUR);
  return d.toLocaleDateString('en-CA');
}

/** `dayKey` for the day `n` logical days before `now`. n=1 is yesterday. */
export function dayKeyOffset(n: number, now: Date = new Date()): string {
  const d = new Date(now);
  d.setDate(d.getDate() - n);
  return dayKey(d);
}

/** Midnight at the START of the logical day's calendar date. */
export function startOfDay(now: Date = new Date()): Date {
  const d = new Date(now);
  d.setHours(d.getHours() - DAY_RESET_HOUR);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** The last instant of the logical day's calendar date, for `due <= endOfDay()`. */
export function endOfDay(now: Date = new Date()): Date {
  const d = startOfDay(now);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * The instant a "due today" test compares against: whichever of endOfDay and
 * now is later, so a card stamped with the current moment during the 00:00–03:00
 * tail of a logical day does not read as not-yet-due.
 */
export function dueThrough(now: Date = new Date()): Date {
  const end = endOfDay(now);
  return end.getTime() >= now.getTime() ? end : now;
}

/** DAY_RESET_HOUR on the day `interval` logical days ahead of the CURRENT day. */
export function dueAfterDays(interval: number, now: Date = new Date()): Date {
  const d = startOfDay(now);
  d.setDate(d.getDate() + interval);
  d.setHours(DAY_RESET_HOUR, 0, 0, 0);
  return d;
}
